import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { X, Menu, Calendar, User, Scissors, ChevronLeft, ChevronRight, Clock, Phone, MapPin, Share2, LogOut } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { he } from 'date-fns/locale';
import { supabase } from '../../lib/supabase';
import OtpDialog from '../../lib/OtpDialog';
import { normalizeIL } from '../../lib/edgeFns';
import { BookingWizard } from '../../components/booking/BookingWizard';
import { AppointmentDetails } from '../../components/booking/AppointmentDetails';
import { CustomerAIChat } from '../../components/booking/CustomerAIChat';
import { CookieConsent } from '../../components/CookieConsent';
import { AccessibilityButton } from '../../components/AccessibilityButton';
import { useLogoSrc } from '../../hooks/useLogoSrc';
import toast from 'react-hot-toast';

import {
  HeroSection,
  WelcomeCard,
  QuickActions,
  AppointmentsSection,
  ServicesShowcase,
  AboutSection,
  TeamSection,
  SocialLinks,
  AIAssistantCard,
} from './components';

import { colors, radius, shadows } from './designSystem';
import { containerVariants, fadeUpVariants, slideFromLeftVariants, backdropVariants, modalVariants, bottomSheetVariants } from './animations';

function parseHHMM(str?: string) {
  if (!str) return undefined;
  const [h, m] = str.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return undefined;
  return h * 60 + m;
}

function rangeOverlap(s1: number, e1: number, s2: number, e2: number) {
  return Math.max(s1, s2) < Math.min(e1, e2);
}

function eventOverlapsBreaks(
  start: Date,
  end: Date,
  breaks: { startTime?: string; endTime?: string; start_time?: string; end_time?: string }[]
) {
  const s = start.getHours() * 60 + start.getMinutes();
  const e = end.getHours() * 60 + end.getMinutes();
  return breaks.some(b => {
    const bs = parseHHMM(b.startTime || b.start_time);
    const be = parseHHMM(b.endTime || b.end_time);
    return bs != null && be != null && rangeOverlap(s, e, bs, be);
  });
}

export default function ExternalPageIOS() {
  const { businessLink } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [businessData, setBusinessData] = useState<any>(null);
  const [settings, setSettings] = useState<any>(null);
  const [showAuth, setShowAuth] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [showHours, setShowHours] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerData, setCustomerData] = useState<any>(null);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [isAutofillMode, setIsAutofillMode] = useState(false);
  const [customerName, setCustomerName] = useState<string>('');
  const [historyAppointments, setHistoryAppointments] = useState<any[]>([]);
  const [historyFilter, setHistoryFilter] = useState<'all' | 'confirmed' | 'canceled' | 'done' | 'booked'>('all');
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    acceptedTerms: false,
  });
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [bookingWizardReady, setBookingWizardReady] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [autofillLoading, setAutofillLoading] = useState(false);

  const mainContentRef = useRef<HTMLDivElement>(null);
  const autofillProcessedRef = useRef(false);

  const logoSrc = useLogoSrc(businessData?.logo_url);

  useEffect(() => {
    const anyModalOpen = showAuth || showBooking || showGallery || showHours ||
                         showChat || showMenu || showHistory || showNewCustomer;

    if (anyModalOpen) {
      const scrollY = window.scrollY;

      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.position = 'fixed';
      document.documentElement.style.top = `-${scrollY}px`;
      document.documentElement.style.width = '100%';
      document.documentElement.style.height = '100%';

      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';

      if (mainContentRef.current) {
        mainContentRef.current.style.overflow = 'hidden';
        mainContentRef.current.style.position = 'fixed';
        mainContentRef.current.style.top = '0';
        mainContentRef.current.style.width = '100%';
        mainContentRef.current.style.height = '100vh';
      }
    } else {
      const scrollY = document.body.style.top;

      document.documentElement.style.overflow = '';
      document.documentElement.style.position = '';
      document.documentElement.style.top = '';
      document.documentElement.style.width = '';
      document.documentElement.style.height = '';

      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.height = '';

      if (mainContentRef.current) {
        mainContentRef.current.style.overflow = '';
        mainContentRef.current.style.position = '';
        mainContentRef.current.style.top = '';
        mainContentRef.current.style.width = '';
        mainContentRef.current.style.height = '';
      }

      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      document.documentElement.style.overflow = '';
      document.documentElement.style.position = '';
      document.documentElement.style.top = '';
      document.documentElement.style.width = '';
      document.documentElement.style.height = '';

      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.height = '';

      if (mainContentRef.current) {
        mainContentRef.current.style.overflow = '';
        mainContentRef.current.style.position = '';
        mainContentRef.current.style.top = '';
        mainContentRef.current.style.width = '';
        mainContentRef.current.style.height = '';
      }
    };
  }, [showAuth, showBooking, showGallery, showHours, showChat, showMenu, showHistory, showNewCustomer]);

  const getDayBreaks = (date: Date) => {
    if (!businessData?.business_hours?.regular_hours) return [];
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDayName = dayNames[date.getDay()];
    const todayHours = businessData.business_hours.regular_hours[currentDayName];
    if (todayHours && Array.isArray(todayHours.breaks)) {
      return todayHours.breaks.map((b: any) => ({
        startTime: b.startTime || b.start_time,
        endTime: b.endTime || b.end_time
      }));
    }
    return [];
  };

  useEffect(() => {
    if (businessData?.id) {
      setBookingWizardReady(true);
    } else {
      setBookingWizardReady(false);
    }
  }, [businessData?.id]);

  // בדיקה אם המשתמש התחבר והצריך לחזור ל-URL המלא
  useEffect(() => {
    const returnUrl = sessionStorage.getItem('returnToAfterAuth');
    const customerPhone = localStorage.getItem('customerPhone');
    const currentUrl = window.location.href;

    // אם יש URL שמור והמשתמש מחובר וה-URL הנוכחי לא תואם - חזור לשם
    if (returnUrl && customerPhone && currentUrl !== returnUrl) {
      sessionStorage.removeItem('returnToAfterAuth');
      setTimeout(() => {
        window.location.href = returnUrl;
      }, 100);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const preventZoom = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    const preventDoubleTapZoom = (e: TouchEvent) => {
      e.preventDefault();
    };

    document.addEventListener('touchstart', preventZoom, { passive: false });
    document.addEventListener('touchmove', preventZoom, { passive: false });
    document.addEventListener('gesturestart', preventDoubleTapZoom as any);
    document.addEventListener('gesturechange', preventDoubleTapZoom as any);
    document.addEventListener('gestureend', preventDoubleTapZoom as any);

    return () => {
      document.removeEventListener('touchstart', preventZoom);
      document.removeEventListener('touchmove', preventZoom);
      document.removeEventListener('gesturestart', preventDoubleTapZoom as any);
      document.removeEventListener('gesturechange', preventDoubleTapZoom as any);
      document.removeEventListener('gestureend', preventDoubleTapZoom as any);
    };
  }, []);

  useEffect(() => {
    if (businessLink) {
      loadBusinessData();
    }
  }, [businessLink]);

  useEffect(() => {
    if (businessData?.id) {
      checkAuth();
    }
  }, [businessData?.id]);

  useEffect(() => {
    if (customerId && businessData?.id) {
      loadAppointments();
    }
  }, [customerId, businessData?.id]);

  useEffect(() => {
    async function checkAiAvailability() {
      try {
        if (!businessData?.id) return;

        const { data, error } = await supabase
          .from('subscriptions')
          .select('plan_id')
          .eq('business_id', businessData.id)
          .eq('status', 'active')
          .single();

        if (error) {

          setAiEnabled(false);
          return;
        }

        const basicPlanId = '686adae1-c8ab-46a8-aea4-d5a78e431723';
        setAiEnabled(data?.plan_id !== basicPlanId);
      } catch (error) {

        setAiEnabled(false);
      }
    }

    checkAiAvailability();
  }, [businessData?.id]);

  // Autofill from WhatsApp link
  useEffect(() => {
    console.log('[AutofillModal] useEffect triggered', {
      processed: autofillProcessedRef.current,
      hasBusinessData: !!businessData?.id,
      searchParamsString: searchParams.toString()
    });

    if (autofillProcessedRef.current) {
      console.log('[AutofillModal] Already processed, skipping');
      return;
    }

    if (!businessData?.id) {
      console.log('[AutofillModal] Business data not ready yet');
      return;
    }

    const autofill = searchParams.get('autofill');
    const appointmentId = searchParams.get('appointmentId');
    const businessIdParam = searchParams.get('businessId');

    console.log('[AutofillModal] Parsed params:', { autofill, appointmentId, businessIdParam, businessDataId: businessData.id });

    if (autofill === '1' && appointmentId) {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(appointmentId)) {
        console.log('[AutofillModal] Invalid appointmentId format');
        return;
      }

      // Verify businessId matches if provided
      if (businessIdParam && businessIdParam !== businessData.id) {
        console.log('[AutofillModal] BusinessId mismatch');
        toast.error('התור אינו שייך לעסק זה');
        return;
      }

      autofillProcessedRef.current = true;
      setAutofillLoading(true);

      (async () => {
        try {
          console.log('[AutofillModal] Starting autofill process');
          const appointmentData = await fetchAppointmentById(appointmentId);

          if (appointmentData) {
            console.log('[AutofillModal] Opening modal with appointment data');

            const isAutofill = true; // We're in autofill flow
            const isCanceled = appointmentData.status === 'canceled';
            const appointmentTime = new Date(appointmentData.start_time);
            const isFuture = appointmentTime.getTime() > Date.now();

            // If canceled but NOT autofill → show error
            // If canceled AND autofill → allow (this is the slot to claim)
            if (isCanceled && !isAutofill) {
              toast.error('התור בוטל ואינו זמין יותר', {
                duration: 5000,
                style: { background: '#fff', color: '#dc2626', fontWeight: 700, border: '1px solid #fca5a5' },
              });
              setAutofillLoading(false);
              return;
            }

            // For autofill, if it's canceled but in the past → still show error
            if (isCanceled && isAutofill && !isFuture) {
              toast.error('התור עבר ואינו זמין יותר', {
                duration: 5000,
                style: { background: '#fff', color: '#dc2626', fontWeight: 700, border: '1px solid #fca5a5' },
              });
              setAutofillLoading(false);
              return;
            }

            // For non-canceled appointments, check status
            if (!isCanceled && (appointmentData.status === 'done' || appointmentData.status === 'completed')) {
              toast.error('התור כבר התבצע', {
                duration: 5000,
                style: { background: '#fff', color: '#dc2626', fontWeight: 700, border: '1px solid #fca5a5' },
              });
              setAutofillLoading(false);
              return;
            }

            // Check if appointment is in the past (for non-canceled appointments)
            if (!isCanceled && !isFuture) {
              toast.error('התור עבר ואינו זמין יותר', {
                duration: 5000,
                style: { background: '#fff', color: '#dc2626', fontWeight: 700, border: '1px solid #fca5a5' },
              });
              setAutofillLoading(false);
              return;
            }

            setSelectedAppointment(appointmentData);
            setIsAutofillMode(true);
            toast.success('התור נמצא! בדוק את הפרטים', {
              duration: 3000,
              icon: '✨',
            });


          } else {
            console.log('[AutofillModal] No appointment data found');
            toast.error('התור לא נמצא או שכבר תפוס', {
              duration: 5000,
              style: { background: '#fff', color: '#dc2626', fontWeight: 700, border: '1px solid #fca5a5' },
            });
          }
        } catch (error) {
          console.error('[AutofillModal] Error fetching appointment:', error);
          toast.error('שגיאה בטעינת התור, נסה שוב', {
            duration: 5000,
            style: { background: '#fff', color: '#dc2626', fontWeight: 700, border: '1px solid #fca5a5' },
          });
        } finally {
          setAutofillLoading(false);
        }
      })();
    }
  }, [businessData?.id, searchParams, businessLink, navigate]);

  const loadBusinessData = async () => {
    try {
      setError(null);
      setLoading(true);

      const { data: business, error: businessError } = await supabase
        .from('businesses')
        .select(`
          *,
          business_hours (
            regular_hours,
            special_dates
          )
        `)
        .eq('booking_link', businessLink)
        .single();

      if (businessError) throw new Error('שגיאה בטעינת נתוני העסק');
      if (!business) throw new Error('העסק לא נמצא');

      setBusinessData(business);
      setSettings(business.external_page_settings);

      // שמירת businessId ב-localStorage כדי לתמוך בלקוחות שפותחים את הלינק ישירות
      localStorage.setItem('businessId', business.id);

      // טעינת צוות ושירותים
      await loadTeamMembers(business.id);
      await loadServices(business.id);
    } catch (error: any) {

      setError(error.message || 'שגיאה בטעינת נתוני העסק');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async (businessId: string) => {
    try {
      const { data: team, error: teamError } = await supabase
        .from('users')
        .select('id, name, title, description, profile_image_url, specialties, phone, email, settings, role')
        .eq('business_id', businessId)
        .in('role', ['admin', 'staff']);

      if (teamError) {

        return;
      }

      // סנן רק אנשי צוות שמסומנים כ-visible_in_public
      const visibleTeam = (team || []).filter((member: any) => {
        const settings = typeof member.settings === 'string'
          ? JSON.parse(member.settings)
          : member.settings;

        // מנהל תמיד מוצג, אלא אם כן סימן במפורש שלא
        if (member.role === 'admin') {
          return settings?.visible_in_public !== false;
        }

        // איש צוות - רק אם סומן כ-visible
        return settings?.visible_in_public === true;
      });

      setTeamMembers(visibleTeam);
    } catch (error) {

    }
  };

  const loadServices = async (businessId: string) => {
    try {

      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select('id, name_he, description, image_url, price, duration')
        .eq('business_id', businessId)
        .order('name_he', { ascending: true });

      if (servicesError) {

        return;
      }

      setServices(servicesData || []);
    } catch (error) {

    }
  };

  const checkAuth = async () => {
    try {
      const customerPhone = localStorage.getItem('customerPhone');
      if (!customerPhone) {
        setIsAuthenticated(false);
        setCustomerId(null);
        return;
      }

      let normalizedPhone = customerPhone;
      if (/^0\d{9}$/.test(normalizedPhone)) {
        normalizedPhone = '+972' + normalizedPhone.slice(1);
      }
      normalizedPhone = normalizedPhone.replace(/\s/g, '');

      let res;
      if (normalizedPhone) {
        res = await supabase
          .from('customers')
          .select('id, name, is_blocked')
          .eq('business_id', businessData.id)
          .eq('phone', normalizedPhone)
          .maybeSingle();
      }

      if (!res?.data) {
        let localPhone = normalizedPhone;
        if (normalizedPhone.startsWith('+972') && normalizedPhone.length === 13) {
          localPhone = '0' + normalizedPhone.slice(4);
        }

        res = await supabase
          .from('customers')
          .select('id, name, is_blocked')
          .eq('business_id', businessData.id)
          .eq('phone', customerPhone)
          .maybeSingle();

        if (!res?.data && localPhone) {
          res = await supabase
            .from('customers')
            .select('id, name, is_blocked')
            .eq('business_id', businessData.id)
            .eq('phone', localPhone)
            .maybeSingle();
        }
      }

      const customer = res?.data;
      const customerError = res?.error;

      if (customerError) {
        localStorage.removeItem('customerPhone');
        setIsAuthenticated(false);
        setCustomerId(null);
        toast.error('שגיאה באימות לקוח. נסה שוב.');
        return;
      }

      if (!customer) {
        setShowNewCustomer(true);
        setNewCustomerData({
          firstName: '',
          lastName: '',
          email: '',
          phone: customerPhone,
        });
        setIsAuthenticated(false);
        setCustomerId(null);
        return;
      }

      if (customer.is_blocked) {
        localStorage.removeItem('customerPhone');
        setIsAuthenticated(false);
        setCustomerId(null);
        toast.error('הגישה שלך נחסמה על ידי העסק. אנא פנה לעסק לפרטים נוספים.');
        return;
      }

      setIsAuthenticated(true);
      setCustomerId(customer.id);
      setCustomerName(customer.name);
      setCustomerData(customer);
    } catch (error) {
      setIsAuthenticated(false);
      setCustomerId(null);
      toast.error('שגיאה באימות לקוח. נסה שוב.');
    }
  };

  const loadAppointments = async () => {
    if (!customerId || !businessData?.id) return;

    try {
      setLoadingAppointments(true);

      const { data, error } = await supabase
        .from('appointments')
        .select(`
          id,
          start_time,
          end_time,
          status,
          services (
            id,
            name_he,
            price,
            image_url,
            promotion
          ),
          users (
            id,
            name
          )
        `)
        .eq('customer_id', customerId)
        .eq('business_id', businessData.id)
        .order('start_time');

      if (error) throw new Error('שגיאה בטעינת התורים');

      const now = new Date();
      const filtered = (data || []).filter((apt: any) => {
        const start = parseISO(apt.start_time);
        return (
          start >= now &&
          (apt.status === 'booked' || apt.status === 'confirmed')
        );
      });

      setAppointments(filtered);
    } catch (error: any) {

      toast.error(error.message || 'שגיאה בטעינת התורים');
    } finally {
      setLoadingAppointments(false);
    }
  };

  const loadHistoryAppointments = async () => {
    if (!customerId || !businessData?.id) return;
    try {
      const { data: appointmentsData, error } = await supabase
        .from('appointments')
        .select(`
          id,
          start_time,
          end_time,
          status,
          services (
            id,
            name_he,
            price,
            image_url,
            promotion
          ),
          users (
            id,
            name
          )
        `)
        .eq('customer_id', customerId)
        .eq('business_id', businessData.id)
        .order('start_time', { ascending: false })
        .limit(50);

      if (error) throw new Error('שגיאה בטעינת היסטוריית התורים');

      const appointmentIds = (appointmentsData || []).map((apt: any) => apt.id);
      let invoiceMap: Record<string, string> = {};
      if (appointmentIds.length > 0) {
        const { data: payments } = await supabase
          .from('payment_transactions')
          .select('appointment_id, invoice_link, status')
          .in('appointment_id', appointmentIds)
          .eq('status', 'success');

        if (payments) {
          payments.forEach((p: any) => {
            if (p.appointment_id && p.invoice_link) {
              invoiceMap[p.appointment_id] = p.invoice_link;
            }
          });
        }
      }

      const appointmentsWithInvoice = (appointmentsData || []).map((apt: any) => ({
        ...apt,
        invoice_url: invoiceMap[apt.id] || null
      }));

      setHistoryAppointments(appointmentsWithInvoice);
    } catch (error) {
      toast.error('שגיאה בטעינת היסטוריית התורים');
    }
  };

  const fetchAppointmentById = async (appointmentId: string) => {
    if (!businessData?.id) {
      console.log('[AutofillModal] Business data not ready yet');
      return null;
    }

    console.log('[AutofillModal] Fetching appointment:', appointmentId);

    try {
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          id,
          start_time,
          end_time,
          status,
          services (
            id,
            name_he,
            price,
            image_url,
            promotion,
            description
          ),
          users (
            id,
            name
          )
        `)
        .eq('id', appointmentId)
        .eq('business_id', businessData.id)
        .maybeSingle();

      if (error) {
        console.error('[AutofillModal] Fetch error:', error);
        return null;
      }

      if (!data) {
        console.log('[AutofillModal] Appointment not found or not for this business');
        return null;
      }

      console.log('[AutofillModal] Appointment fetched successfully:', data);
      return data;
    } catch (error) {
      console.error('[AutofillModal] Unexpected error:', error);
      return null;
    }
  };

  const handleBookingSuccess = async (appointmentId: string, appointmentData?: any) => {
    if (appointmentData && businessData?.business_hours) {
      const start = new Date(appointmentData.start_time);
      const end = new Date(appointmentData.end_time);
      const breaks = getDayBreaks(start);
      if (eventOverlapsBreaks(start, end, breaks)) {
        toast.error('אי אפשר לקבוע תור בתוך זמן הפסקה', {
          style: { background: '#fff', color: '#b45309', fontWeight: 700, border: '1px solid #fcd34d' },
          icon: '☕'
        });
        return;
      }
    }
    try {
      await loadAppointments();
      setShowBooking(false);
      toast.success('התור נקבע בהצלחה!');
    } catch (error) {
      toast.error('שגיאה ברענון התורים לאחר קביעת תור');

    }
  };

  const handleCancelAppointment = async (appointmentId: string) => {
    try {
      const customerPhone = localStorage.getItem('customerPhone');
      if (!customerPhone) {
        toast.error('לא נמצא מספר טלפון של הלקוח');
        throw new Error('לא נמצא מספר טלפון של הלקוח');
      }

      console.log('[handleCancelAppointment] Starting cancel with:', {
        appointmentId,
        customerPhone,
        customerId
      });

      // Normalize phone number before sending to RPC
      const normalizedPhone = normalizeIL(customerPhone);
      console.log('[handleCancelAppointment] Normalized phone:', normalizedPhone);

      const { data, error } = await supabase.rpc('cancel_appointment_customer', {
  p_appointment_id: appointmentId,
  p_customer_id: customerId,
  p_reason: 'ביטול ביוזמת הלקוח',
});

      if (error) {
        console.error('[handleCancelAppointment] RPC error:', error);

        // Refresh appointments list to sync with server state
        await loadAppointments();
        setSelectedAppointment(null);

        // Show user-friendly error message
        const errorMessage = error.message || '';
        if (errorMessage.includes('לא נמצא') || errorMessage.includes('כבר בוטל') || errorMessage.includes('הושלם')) {
          toast.error('התור כבר בוטל או הושלם. רשימת התורים עודכנה.');
        } else {
          toast.error('לא ניתן לבטל את התור. נא לפנות לעסק לקבלת עזרה.');
        }
        return;
      }

      console.log('[handleCancelAppointment] Success:', data);
      await loadAppointments();
      setSelectedAppointment(null);
      toast.success('התור בוטל בהצלחה!');
    } catch (error: any) {
      console.error('[handleCancelAppointment] Exception:', error);

      // Refresh appointments on error
      await loadAppointments();
      setSelectedAppointment(null);

      toast.error('שגיאה בביטול התור. רשימת התורים עודכנה.');
    }
  };

  const handleCall = () => {
    if (businessData?.contact_info?.phone) {
      window.open(`tel:${businessData.contact_info.phone.replace(/\D/g, '')}`, '_blank');
    } else {
      toast.error('לא הוגדר מספר טלפון לעסק');
    }
  };

  const handleNavigate = () => {
    if (businessData?.contact_info?.address) {
      const address = encodeURIComponent(businessData.contact_info.address);
      window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
    } else {
      toast.error('לא הוגדרה כתובת לעסק');
    }
  };

  const handleSocial = (platform: 'instagram' | 'whatsapp' | 'facebook' | 'tiktok' | 'youtube') => {
    const url = settings.social[platform];
    if (!url) {
      toast.error(`לא הוגדר קישור ל-${platform}`);
      return;
    }

    if (platform === 'whatsapp') {
      const phone = url.replace(/\D/g, '');
      if (!phone) {
        toast.error('מספר טלפון לא תקין ל-WhatsApp');
        return;
      }
      window.open(`https://wa.me/${phone}`, '_blank');
    } else if (platform === 'facebook' || platform === 'tiktok' || platform === 'youtube') {
      window.open(url, '_blank');
    } else {
      if (!url.startsWith('http')) {
        toast.error(`קישור לא תקין ל-${platform}`);
        return;
      }
      window.open(url, '_blank');
    }
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? galleryItems.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === galleryItems.length - 1 ? 0 : prev + 1
    );
  };

  const handleLogout = () => {
    localStorage.removeItem('customerPhone');
    setIsAuthenticated(false);
    setCustomerId(null);
    setCustomerData(null);
    setAppointments([]);
    toast.success('התנתקת בהצלחה');
  };

  const handleCreateCustomer = async () => {
    if (!businessData?.id) return;
    const { firstName, lastName, email, phone, acceptedTerms } = newCustomerData;
    if (!firstName || !lastName || !phone) {
      toast.error('נא למלא שם, שם משפחה וטלפון');
      return;
    }
    if (!acceptedTerms) {
      toast.error('יש לאשר את תנאי השימוש ומדיניות הפרטיות');
      return;
    }
    setCreatingCustomer(true);
    try {
      const { data, error } = await supabase.from('customers').insert([
        {
          name: `${firstName} ${lastName}`,
          phone,
          email,
          business_id: businessData.id,
        },
      ]).select('id, name').single();
      if (error) {
        toast.error(error.message || 'שגיאה ביצירת לקוח');
        setCreatingCustomer(false);
        return;
      }
      setShowNewCustomer(false);
      setIsAuthenticated(true);
      setCustomerId(data.id);
      setCustomerName(data.name);
      setCustomerData(data);
      toast.success('נרשמת בהצלחה!');
      await loadAppointments();
    } catch (err) {
      toast.error('שגיאה ביצירת לקוח');
    } finally {
      setCreatingCustomer(false);
    }
  };

  function onOtpSuccess(phone: string) {
    const normalized = normalizeIL(phone);
    localStorage.setItem("customerPhone", normalized);
    setIsAuthenticated(true);
    checkAuth();
    toast.success('התחברת בהצלחה!');
  }

  const isStaffVisible = (user: any) => {
    if (!user?.settings) return true;
    try {
      const settings = typeof user.settings === 'string' ? JSON.parse(user.settings) : user.settings;
      return settings.visible_in_public !== false;
    } catch {
      return true;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.secondary }}>
        <div
          className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: colors.accent }}
        />
      </div>
    );
  }

  if (error || !businessData || !settings) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-4" style={{ backgroundColor: colors.secondary }}>
        <p className="text-center" style={{ color: colors.textSecondary }}>{error || 'העסק לא נמצא'}</p>
      </div>
    );
  }

  const galleryItems = Array.isArray(settings?.gallery?.items) ? settings.gallery.items : [];
  const social = typeof settings?.social === 'object' && settings.social !== null ? settings.social : {};
  const aboutContent = typeof settings?.about === 'object' && settings.about && typeof settings.about.content === 'string'
    ? settings.about.content
    : '';

  const daysOrder = [
    { key: 'sunday', label: 'ראשון' },
    { key: 'monday', label: 'שני' },
    { key: 'tuesday', label: 'שלישי' },
    { key: 'wednesday', label: 'רביעי' },
    { key: 'thursday', label: 'חמישי' },
    { key: 'friday', label: 'שישי' },
    { key: 'saturday', label: 'שבת' }
  ];

  return (
    <div ref={mainContentRef} className="min-h-screen pb-72 smooth-scroll" style={{ backgroundColor: colors.secondary }} role="main">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[999] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg"
        aria-label="דלג לתוכן הראשי"
      >
        דלג לתוכן הראשי
      </a>

      {/* Autofill Loading Overlay */}
      <AnimatePresence>
        {autofillLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999] flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white p-8 text-center"
              style={{
                borderRadius: radius.xl,
                boxShadow: shadows.xl,
                maxWidth: '90%',
                width: '320px',
              }}
            >
              <div className="mb-4">
                <div className="animate-spin rounded-full h-12 w-12 border-4 mx-auto"
                  style={{
                    borderColor: colors.accent,
                    borderTopColor: 'transparent',
                  }}
                />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: colors.textPrimary }}>
                טוען את ההצעה
              </h3>
              <p className="text-sm" style={{ color: colors.textSecondary }}>
                מביא את פרטי התור שלך...
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fixed Menu Button */}
      {isAuthenticated && (
        <nav className="fixed top-6 left-6 z-50" aria-label="תפריט ניווט">
          <motion.button
            aria-label="פתח תפריט"
            aria-expanded={showMenu}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setShowMenu(true);
              setShowHistory(false);
            }}
            className="p-3 backdrop-blur-xl transition-all"
            style={{
              borderRadius: radius.xl,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              boxShadow: shadows.lg,
              border: `1px solid ${colors.border}`,
            }}
          >
            <Menu className="h-5 w-5" style={{ color: colors.textPrimary }} />
          </motion.button>
        </nav>
      )}

      {/* Hero Section */}
      <section id="main-content" aria-label="מידע על העסק">
        <HeroSection
          businessName={businessData.name}
          address={businessData.contact_info?.address}
          logoSrc={logoSrc}
          galleryItems={galleryItems.map((item: any) => ({
            url: item.url,
            type: item.type
          }))}
          onGalleryClick={() => galleryItems.length > 0 && setShowGallery(true)}
          onCall={handleCall}
          onWhatsApp={social.whatsapp ? () => handleSocial('whatsapp') : undefined}
          onNavigate={handleNavigate}
          onShowHours={() => setShowHours(true)}
          onInstagram={social.instagram ? () => handleSocial('instagram') : undefined}
          onFacebook={social.facebook ? () => handleSocial('facebook') : undefined}
          onTikTok={social.tiktok ? () => handleSocial('tiktok') : undefined}
        onYouTube={social.youtube ? () => handleSocial('youtube') : undefined}
      />
      </section>

      {/* Main Content Container - Unified iOS Style */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 mt-6 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white overflow-hidden"
          style={{
            borderRadius: radius['3xl'],
            boxShadow: shadows.xl,
            border: `1px solid ${colors.border}`,
          }}
        >
          {/* Welcome/Auth Card */}
          <WelcomeCard
            isAuthenticated={isAuthenticated}
            customerName={customerName}
            onLogin={() => setShowAuth(true)}
            onLogout={handleLogout}
          />

          {/* Appointments Section or Services Showcase */}
          {isAuthenticated ? (
            <AppointmentsSection
              isAuthenticated={isAuthenticated}
              appointments={appointments}
              loading={loadingAppointments}
              onBookAppointment={() => setShowBooking(true)}
              onAppointmentClick={(apt) => setSelectedAppointment(apt)}
              businessId={businessData.id}
            />
          ) : (
            <ServicesShowcase services={services} />
          )}

          {/* About Section */}
          {aboutContent && <AboutSection content={aboutContent} />}
        </motion.div>

        {/* Team Section - Separated */}
        {teamMembers.length > 0 && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mt-6"
          >
            <TeamSection team={teamMembers} />
          </motion.div>
        )}
      </div>

      {/* AI Assistant Floating Card */}
      {/* Temporarily hidden per user request */}
      {/* <AIAssistantCard
        isEnabled={aiEnabled}
        isAuthenticated={isAuthenticated}
        onOpenChat={() => setShowChat(true)}
        onLogin={() => setShowAuth(true)}
      /> */}

      {/* Side Menu */}
      <AnimatePresence>
        {showMenu && (
          <motion.div
            key="side-menu"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 bg-white z-[100] flex flex-col modal-content"
            style={{
              borderTopLeftRadius: '1.5rem',
              borderTopRightRadius: '1.5rem',
              maxHeight: '75vh',
              boxShadow: '0 -10px 40px rgba(0, 0, 0, 0.1)',
              paddingBottom: 'env(safe-area-inset-bottom)',
              overscrollBehavior: 'contain',
            }}
          >
            {/* Handle Bar */}
            <div className="flex justify-center pt-3 pb-2">
              <div
                className="w-10 h-1 rounded-full"
                style={{ backgroundColor: '#D1D5DB' }}
              />
            </div>

            {/* Header */}
            <div
              className="flex items-center justify-between px-6 py-4"
              style={{ borderBottom: '1px solid #F3F4F6' }}
            >
              <h2 className="font-bold text-xl" style={{ color: '#1C1C1E' }}>תפריט</h2>
              <button
                onClick={() => setShowMenu(false)}
                className="text-base font-medium"
                style={{ color: '#007AFF' }}
              >
                סגור
              </button>
            </div>

            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {/* Profile Section */}
              {isAuthenticated && customerData && (
                <div className="px-4 py-3 rounded-2xl" style={{ backgroundColor: '#F3F4F6' }}>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 flex items-center justify-center rounded-full font-bold text-white text-lg"
                      style={{ backgroundColor: '#007AFF' }}
                    >
                      {customerData.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold" style={{ color: '#1C1C1E' }}>
                        {customerData.name || 'משתמש'}
                      </p>
                      <p className="text-sm" style={{ color: '#8E8E93' }}>
                        {customerData.phone || customerData.email || ''}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Menu Items List */}
              <div className="bg-white border rounded-2xl overflow-hidden" style={{ borderColor: '#E5E7EB' }}>
                {/* History */}
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setShowHistory(true);
                    loadHistoryAppointments();
                  }}
                  className="w-full flex items-center gap-4 px-4 py-3 border-b text-right"
                  style={{ borderColor: '#F3F4F6' }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full" style={{ backgroundColor: '#007AFF15' }}>
                    <Calendar className="h-5 w-5" style={{ color: '#007AFF' }} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold" style={{ color: '#1C1C1E' }}>היסטוריית תורים</p>
                    <p className="text-sm" style={{ color: '#8E8E93' }}>צפה בכל התורים שלך</p>
                  </div>
                  <ChevronLeft className="h-5 w-5" style={{ color: '#C7C7CC' }} />
                </button>

                {/* Contact Business */}
                {businessData?.phone && (
                  <button
                    onClick={() => {
                      window.location.href = `tel:${businessData.phone}`;
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-4 px-4 py-3 border-b text-right"
                    style={{ borderColor: '#F3F4F6' }}
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full" style={{ backgroundColor: '#34C75915' }}>
                      <Phone className="h-5 w-5" style={{ color: '#34C759' }} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold" style={{ color: '#1C1C1E' }}>צור קשר</p>
                      <p className="text-sm" style={{ color: '#8E8E93' }}>{businessData.phone}</p>
                    </div>
                    <ChevronLeft className="h-5 w-5" style={{ color: '#C7C7CC' }} />
                  </button>
                )}

                {/* Business Hours */}
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setShowHours(true);
                  }}
                  className="w-full flex items-center gap-4 px-4 py-3 border-b text-right"
                  style={{ borderColor: '#F3F4F6' }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full" style={{ backgroundColor: '#FF9F0A15' }}>
                    <Clock className="h-5 w-5" style={{ color: '#FF9F0A' }} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold" style={{ color: '#1C1C1E' }}>שעות פעילות</p>
                    <p className="text-sm" style={{ color: '#8E8E93' }}>צפה בשעות הפתיחה</p>
                  </div>
                  <ChevronLeft className="h-5 w-5" style={{ color: '#C7C7CC' }} />
                </button>

                {/* Location */}
                {businessData?.address && (
                  <button
                    onClick={() => {
                      const encodedAddress = encodeURIComponent(businessData.address || '');
                      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-4 px-4 py-3 border-b text-right"
                    style={{ borderColor: '#F3F4F6' }}
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full" style={{ backgroundColor: '#FF3B3015' }}>
                      <MapPin className="h-5 w-5" style={{ color: '#FF3B30' }} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold" style={{ color: '#1C1C1E' }}>מיקום</p>
                      <p className="text-sm truncate" style={{ color: '#8E8E93' }}>{businessData.address}</p>
                    </div>
                    <ChevronLeft className="h-5 w-5" style={{ color: '#C7C7CC' }} />
                  </button>
                )}

                {/* Share */}
                <button
                  onClick={async () => {
                    const shareUrl = window.location.href;
                    if (navigator.share) {
                      try {
                        await navigator.share({
                          title: businessData?.name || 'העסק שלנו',
                          text: `קבע תור ב${businessData?.name || 'העסק שלנו'}`,
                          url: shareUrl,
                        });
                      } catch (err) {

                      }
                    } else {
                      navigator.clipboard.writeText(shareUrl);
                      toast.success('הקישור הועתק ללוח');
                    }
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-4 px-4 py-3 text-right"
                  style={{ borderColor: '#F3F4F6' }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full" style={{ backgroundColor: '#5856D615' }}>
                    <Share2 className="h-5 w-5" style={{ color: '#5856D6' }} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold" style={{ color: '#1C1C1E' }}>שתף</p>
                    <p className="text-sm" style={{ color: '#8E8E93' }}>שתף את העסק עם חברים</p>
                  </div>
                  <ChevronLeft className="h-5 w-5" style={{ color: '#C7C7CC' }} />
                </button>
              </div>

              {/* Logout Button */}
              {isAuthenticated && (
                <button
                  onClick={async () => {
                    try {
                      await supabase.auth.signOut();
                      setCustomerId(null);
                      setCustomerData(null);
                      setShowMenu(false);
                      toast.success('התנתקת בהצלחה');
                    } catch (error) {

                      toast.error('שגיאה בהתנתקות');
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 py-4 font-semibold rounded-2xl"
                  style={{
                    backgroundColor: '#FF3B30',
                    color: '#FFFFFF',
                  }}
                >
                  <LogOut className="h-5 w-5" />
                  <span>התנתק</span>
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* History Modal - iOS Style Bottom Sheet */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] flex items-center justify-center p-4"
            style={{ backgroundColor: colors.overlay }}
            onClick={() => setShowHistory(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed inset-x-0 bottom-0 bg-white w-full max-h-[90vh] flex flex-col modal-content"
              style={{
                borderTopLeftRadius: '1.5rem',
                borderTopRightRadius: '1.5rem',
                boxShadow: '0 -10px 40px rgba(0, 0, 0, 0.1)',
                paddingBottom: 'env(safe-area-inset-bottom)',
                overscrollBehavior: 'contain',
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Handle Bar */}
              <div className="flex justify-center pt-3 pb-2">
                <div
                  className="w-10 h-1 rounded-full"
                  style={{ backgroundColor: '#D1D5DB' }}
                />
              </div>

              {/* Header */}
              <div
                className="flex items-center justify-between px-6 py-4"
                style={{
                  borderBottom: `1px solid #F3F4F6`,
                }}
              >
                <h2 className="font-bold text-xl" style={{ color: '#1C1C1E' }}>היסטוריית תורים</h2>
                <button
                  onClick={() => setShowHistory(false)}
                  className="text-base font-medium"
                  style={{ color: '#007AFF' }}
                >
                  סגור
                </button>
              </div>

              <div className="px-6 py-4" style={{ borderBottom: `1px solid ${colors.border}`, backgroundColor: colors.secondary }}>
                <p className="text-sm font-medium mb-3" style={{ color: colors.textSecondary }}>סנן לפי סטטוס</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'all', label: 'הכל', color: colors.accent },
                    { value: 'confirmed', label: 'מאושר', color: colors.success },
                    { value: 'booked', label: 'ממתין', color: colors.warning },
                    { value: 'canceled', label: 'בוטל', color: colors.error },
                    { value: 'done', label: 'הושלם', color: colors.accent },
                  ].map((filter) => (
                    <button
                      key={filter.value}
                      className="px-4 py-2 text-xs font-semibold transition-all"
                      style={{
                        borderRadius: radius.xl,
                        backgroundColor: historyFilter === filter.value ? filter.color : colors.white,
                        color: historyFilter === filter.value ? colors.white : colors.textSecondary,
                        boxShadow: historyFilter === filter.value ? shadows.md : 'none',
                      }}
                      onClick={() => setHistoryFilter(filter.value as any)}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {!historyAppointments || historyAppointments.length === 0 ? (
                  <div className="text-center py-12">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                      style={{ backgroundColor: colors.secondary }}
                    >
                      <Calendar className="h-8 w-8" style={{ color: colors.textTertiary }} />
                    </div>
                    <p style={{ color: colors.textSecondary }}>אין היסטוריית תורים</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {historyAppointments
                      .filter(apt => {
                        if (historyFilter === 'all') return true;
                        if (historyFilter === 'done') return apt.status === 'done' || apt.status === 'completed';
                        return apt.status === historyFilter;
                      })
                      .map(apt => {
                        const statusConfig = {
                          confirmed: { bg: colors.successLight, text: colors.success, label: "מאושר" },
                          booked: { bg: colors.warningLight, text: colors.warning, label: "ממתין" },
                          canceled: { bg: colors.errorLight, text: colors.error, label: "בוטל" },
                          done: { bg: colors.secondary, text: colors.textSecondary, label: "הושלם" }
                        };
                        const config = statusConfig[apt.status as keyof typeof statusConfig] || statusConfig.booked;

                        const promotion = apt.services?.promotion as any;
                        const hasPromotion = promotion?.is_active && promotion?.discount_value;

                        let displayPrice = apt.services?.price || 0;
                        if (hasPromotion && promotion) {
                          if (promotion.discount_type === 'percentage') {
                            displayPrice = (apt.services?.price || 0) * (1 - (promotion.discount_value / 100));
                          } else {
                            displayPrice = (apt.services?.price || 0) - promotion.discount_value;
                          }
                        }
                        const originalPrice = apt.services?.price;

                        return (
                          <motion.div
                            key={apt.id}
                            whileHover={{ scale: 1.01 }}
                            className="bg-white border overflow-hidden"
                            style={{
                              borderRadius: radius.xl,
                              borderColor: colors.border,
                            }}
                          >
                            <div className="p-4">
                              <div className="flex items-start gap-4">
                                {apt.services?.image_url ? (
                                  <img
                                    src={apt.services.image_url}
                                    alt={apt.services.name_he}
                                    className="w-16 h-16 object-cover flex-shrink-0"
                                    style={{ borderRadius: radius.lg }}
                                  />
                                ) : (
                                  <div
                                    className="w-16 h-16 flex items-center justify-center flex-shrink-0"
                                    style={{
                                      backgroundColor: colors.secondary,
                                      borderRadius: radius.lg,
                                    }}
                                  >
                                    <Scissors className="h-8 w-8" style={{ color: colors.textTertiary }} />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2 mb-1">
                                    <span className="font-bold text-base" style={{ color: colors.textPrimary }}>
                                      {apt.services?.name_he || 'שירות'}
                                    </span>
                                    <span
                                      className="px-2 py-0.5 text-xs font-medium flex-shrink-0"
                                      style={{
                                        backgroundColor: config.bg,
                                        color: config.text,
                                        borderRadius: radius.full,
                                      }}
                                    >
                                      {config.label}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm mb-2" style={{ color: colors.textSecondary }}>
                                    <span>{format(parseISO(apt.start_time), 'd בMMMM, HH:mm', { locale: he })}</span>
                                    <span>•</span>
                                    <span>עם {apt.users?.name || 'ללא שם'}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {hasPromotion ? (
                                      <>
                                        <span className="text-sm line-through" style={{ color: colors.textTertiary }}>
                                          ₪{originalPrice}
                                        </span>
                                        <span className="text-base font-bold" style={{ color: colors.success }}>
                                          ₪{displayPrice}
                                        </span>
                                        <span
                                          className="text-xs px-2 py-0.5"
                                          style={{
                                            backgroundColor: colors.successLight,
                                            color: colors.success,
                                            borderRadius: radius.full,
                                          }}
                                        >
                                          מבצע
                                        </span>
                                      </>
                                    ) : (
                                      displayPrice && (
                                        <span className="text-base font-bold" style={{ color: colors.textPrimary }}>
                                          ₪{displayPrice}
                                        </span>
                                      )
                                    )}
                                  </div>
                                  {(historyFilter === 'done' || (apt.status === 'done' || apt.status === 'completed')) && apt.invoice_url && (
                                    <div className="mt-3">
                                      <a
                                        href={apt.invoice_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold transition-all"
                                        style={{
                                          backgroundColor: colors.accent,
                                          color: colors.white,
                                          borderRadius: radius.lg,
                                        }}
                                      >
                                        הצג חשבונית
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gallery Modal */}
      <AnimatePresence>
        {showGallery && (
          <motion.div
            key="gallery-modal"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 z-50 flex items-center"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
          >
            <div className="relative w-full h-full flex items-center">
              <button
                onClick={() => setShowGallery(false)}
                className="absolute top-4 right-4 p-2 text-white z-10"
              >
                <X className="h-6 w-6" />
              </button>

              {galleryItems.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-4 p-2 text-white z-10"
                  >
                    <ChevronLeft className="h-8 w-8" />
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-4 p-2 text-white z-10"
                  >
                    <ChevronRight className="h-8 w-8" />
                  </button>
                </>
              )}

              <div className="w-full h-full flex items-center justify-center p-4">
                {galleryItems[currentImageIndex]?.type === 'video' ? (
                  <video
                    src={galleryItems[currentImageIndex].url}
                    className="max-w-full max-h-full object-contain"
                    controls
                    autoPlay
                    playsInline
                  />
                ) : (
                  <img
                    src={galleryItems[currentImageIndex]?.url}
                    alt=""
                    className="max-w-full max-h-full object-contain"
                  />
                )}
              </div>

              {galleryItems.length > 1 && (
                <div
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 text-white text-sm"
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    borderRadius: radius.full,
                  }}
                >
                  {currentImageIndex + 1} / {galleryItems.length}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Hours Modal - iOS Style */}
        {showHours && businessData?.business_hours && (
          <motion.div
            key="hours-modal"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 z-50 flex items-end"
            style={{ backgroundColor: colors.overlay }}
            onClick={() => setShowHours(false)}
          >
            <motion.div
              variants={bottomSheetVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-white w-full overflow-hidden modal-content"
              style={{
                borderTopLeftRadius: radius['3xl'],
                borderTopRightRadius: radius['3xl'],
                boxShadow: '0 -10px 40px rgba(0, 0, 0, 0.15)',
                maxHeight: '80vh',
                paddingBottom: 'env(safe-area-inset-bottom)',
                overscrollBehavior: 'contain',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Handle Bar */}
              <div className="flex justify-center pt-2 pb-1">
                <div
                  className="w-10 h-1 rounded-full"
                  style={{ backgroundColor: colors.divider }}
                />
              </div>

              <div
                className="px-6 py-4"
                style={{
                  borderBottom: `1px solid ${colors.divider}`,
                  backgroundColor: colors.white,
                }}
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>שעות פעילות</h2>
                  <button
                    onClick={() => setShowHours(false)}
                    className="text-base font-medium"
                    style={{ color: colors.accent }}
                  >
                    סגור
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-2 max-h-[70vh] overflow-y-auto">
                {daysOrder.map(dayObj => {
                  const hours = businessData.business_hours?.regular_hours?.[dayObj.key];
                  if (!hours) {
                    return (
                      <div
                        key={dayObj.key}
                        className="p-4 space-y-2"
                        style={{
                          backgroundColor: colors.secondary,
                          borderRadius: radius.xl,
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold" style={{ color: colors.textPrimary }}>{dayObj.label}</span>
                          <span
                            className="px-3 py-1 text-sm font-medium"
                            style={{
                              backgroundColor: colors.errorLight,
                              color: colors.error,
                              borderRadius: radius.full,
                            }}
                          >
                            סגור
                          </span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={dayObj.key}
                      className="px-4 py-3 border-b"
                      style={{
                        borderColor: colors.divider,
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium" style={{ color: colors.textPrimary }}>{dayObj.label}</span>
                        {!hours.isActive && !hours.is_active ? (
                          <span
                            className="text-sm font-medium"
                            style={{
                              color: colors.textSecondary,
                            }}
                          >
                            סגור
                          </span>
                        ) : (
                          <span className="font-medium" style={{ color: colors.textSecondary }}>
                            {hours.startTime || hours.start_time} - {hours.endTime || hours.end_time}
                          </span>
                        )}
                      </div>
                      {(hours.isActive || hours.is_active) && Array.isArray(hours.breaks) && hours.breaks.length > 0 && (
                        <div className="mt-2">
                          {hours.breaks.map((breakItem: any, index: number) => (
                            <div key={index} className="text-xs" style={{ color: colors.textTertiary }}>
                              הפסקה: {breakItem.startTime || breakItem.start_time} - {breakItem.endTime || breakItem.end_time}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {businessData.business_hours?.special_dates &&
                  businessData.business_hours.special_dates
                  .filter((date: any) => new Date(date.date) >= new Date(new Date().setHours(0,0,0,0)))
                  .length > 0 && (
                  <div className="pt-3">
                    <div className="px-4 py-2" style={{ backgroundColor: colors.secondary }}>
                      <h3 className="font-semibold text-sm" style={{ color: colors.textSecondary }}>ימים מיוחדים</h3>
                    </div>
                    <div>
                      {businessData.business_hours.special_dates
                        .filter((date: any) => new Date(date.date) >= new Date(new Date().setHours(0,0,0,0)))
                        .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
                        .map((date: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-center justify-between px-4 py-3 border-b"
                            style={{
                              borderColor: colors.divider,
                            }}
                          >
                            <span className="font-medium" style={{ color: colors.textPrimary }}>
                              {new Date(date.date).toLocaleDateString('he-IL')}
                            </span>
                            {date.is_closed ? (
                              <span className="text-sm font-medium" style={{ color: colors.textSecondary }}>
                                סגור
                              </span>
                            ) : (
                              <span className="font-medium" style={{ color: colors.textSecondary }}>
                                {date.start_time} - {date.end_time}
                              </span>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}

        <OtpDialog
          open={showAuth}
          onClose={() => setShowAuth(false)}
          onSuccess={onOtpSuccess}
          defaultPhone={localStorage.getItem("customerPhone") || ""}
        />

        {showBooking && businessData?.id && bookingWizardReady && (
          <div key="booking-modal" className="fixed inset-0 flex items-center justify-center bg-black/40"
            style={{ zIndex: 9999 }}
          >
            <div
              className="
                bg-white
                w-full max-w-xs sm:max-w-md md:max-w-lg
                mx-auto p-2
                flex flex-col
                max-h-[70vh]
                overflow-y-auto
              "
              style={{
                margin: '16px',
                pointerEvents: 'auto',
                borderRadius: radius.xl,
                boxShadow: shadows['2xl'],
              }}
            >
              <BookingWizard
                key={businessData.id}
                businessId={businessData.id}
                onClose={() => setShowBooking(false)}
                onSuccess={handleBookingSuccess}
                staffFilter={isStaffVisible}
              />
            </div>
          </div>
        )}

        {selectedAppointment && (
          <AppointmentDetails
            key="appointment-details-modal"
            appointment={selectedAppointment}
            onClose={() => {
              setSelectedAppointment(null);
              setIsAutofillMode(false);
            }}
            onCancel={handleCancelAppointment}
            isAutofillOffer={isAutofillMode}
            businessId={businessData?.id}
            bookingLink={businessLink}
          />
        )}

        {showChat && businessData?.id && (
          <CustomerAIChat
            key="chat-modal"
            businessId={businessData.id}
            customerId={customerId}
            onClose={() => setShowChat(false)}
          />
        )}

        {showNewCustomer && (
          <div key="new-customer-modal" className="fixed z-[200] inset-0 flex items-center justify-center">
            <div className="fixed inset-0" style={{ backgroundColor: colors.overlay }} aria-hidden="true" onClick={() => setShowNewCustomer(false)} />
            <div
              className="relative bg-white max-w-sm w-full p-6 z-10"
              style={{
                borderRadius: radius.xl,
                boxShadow: shadows.xl,
              }}
            >
              <div className="text-lg font-bold mb-2" style={{ color: colors.accent }}>הרשמה כלקוח חדש</div>
              <div className="space-y-3">
                <input
                  className="w-full border px-3 py-2"
                  placeholder="שם פרטי"
                  value={newCustomerData.firstName}
                  onChange={e => setNewCustomerData(d => ({ ...d, firstName: e.target.value }))}
                  autoFocus
                  style={{
                    borderRadius: radius.lg,
                    borderColor: colors.border,
                  }}
                />
                <input
                  className="w-full border px-3 py-2"
                  placeholder="שם משפחה"
                  value={newCustomerData.lastName}
                  onChange={e => setNewCustomerData(d => ({ ...d, lastName: e.target.value }))}
                  style={{
                    borderRadius: radius.lg,
                    borderColor: colors.border,
                  }}
                />
                <input
                  className="w-full border px-3 py-2"
                  placeholder="אימייל (לא חובה)"
                  type="email"
                  value={newCustomerData.email}
                  onChange={e => setNewCustomerData(d => ({ ...d, email: e.target.value }))}
                  style={{
                    borderRadius: radius.lg,
                    borderColor: colors.border,
                  }}
                />
                <input
                  className="w-full border px-3 py-2"
                  placeholder="טלפון"
                  value={newCustomerData.phone}
                  onChange={e => setNewCustomerData(d => ({ ...d, phone: e.target.value }))}
                  disabled
                  style={{
                    borderRadius: radius.lg,
                    borderColor: colors.border,
                    backgroundColor: colors.secondary,
                  }}
                />

                {/* Terms and Privacy Consent */}
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={newCustomerData.acceptedTerms}
                    onChange={e => setNewCustomerData(d => ({ ...d, acceptedTerms: e.target.checked }))}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    aria-label="אישור תנאי שימוש ומדיניות פרטיות"
                  />
                  <label htmlFor="acceptTerms" className="text-sm text-gray-700 leading-relaxed">
                    אני מאשר/ת שקראתי והבנתי את{' '}
                    <a
                      href="/book/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-medium"
                    >
                      תנאי השימוש
                    </a>
                    {' '}ואת{' '}
                    <a
                      href="/book/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-medium"
                    >
                      מדיניות הפרטיות
                    </a>
                    , כולל השימוש בעוגיות (Cookies) לשיפור השירות.
                  </label>
                </div>
              </div>
              <button
                className="w-full mt-4 py-2 text-white font-bold transition"
                onClick={handleCreateCustomer}
                disabled={creatingCustomer || !newCustomerData.acceptedTerms}
                style={{
                  background: `linear-gradient(to right, ${colors.accent}, ${colors.accentDark})`,
                  borderRadius: radius.lg,
                  boxShadow: shadows.md,
                }}
              >
                {creatingCustomer ? 'נרשם...' : 'הרשמה'}
              </button>
              <button
                className="w-full mt-2 py-2 font-bold transition"
                onClick={() => setShowNewCustomer(false)}
                style={{
                  backgroundColor: colors.secondary,
                  color: colors.textPrimary,
                  borderRadius: radius.lg,
                  boxShadow: shadows.sm,
                }}
              >
                ביטול
              </button>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="mt-16 pb-8 px-4">
        <div className="max-w-5xl mx-auto">
          {/* CTA Button */}
          <div className="text-center mb-8">
            <a
              href="https://www.loyalflow.co.il"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              פתחו עמוד כזה לעסק שלכם
            </a>
          </div>

          {/* Logo and Links */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-2">
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                LoyalFlow
              </span>
            </div>

            <div className="flex items-center justify-center gap-4 text-xs" style={{ color: colors.textSecondary }}>
              <a
                href="/book/privacy-policy"
                className="hover:underline transition-colors"
                style={{ color: colors.textSecondary }}
              >
                מדיניות פרטיות
              </a>
              <span>•</span>
              <a
                href="/book/terms-of-service"
                className="hover:underline transition-colors"
                style={{ color: colors.textSecondary }}
              >
                תנאי שימוש
              </a>
            </div>

            <div className="text-xs" style={{ color: colors.textSecondary }}>
              © {new Date().getFullYear()} LoyalFlow. כל הזכויות שמורות.
            </div>
          </div>
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      <CookieConsent />

      {/* Accessibility Button */}
      <AccessibilityButton />
    </div>
  );
}
